import { useEffect, useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import store from '../../reducers/storeRegistration';
import appHistory from '../../utils/appHistory';
import { resolveDefaultLandingPageUrl } from '../../utils/userDefaultLanding';
import { readUserPreferencesContentForDefaultLanding } from '../favorites/userPreferencesFavorites';
import { finishLandingCheck, getLandingGateSnapshot, resetLandingGate, startLandingCheck } from './postAuthLandingGate';
const sleep = ms => new Promise(r => {
  setTimeout(r, ms);
});
const SAVED_VIEW_POLL_MS = 80;
const SAVED_VIEW_POLL_COUNT = 8;
const getSavedViewsFromStore = () => {
  var _store$getState$user;
  return ((_store$getState$user = store.getState().user) === null || _store$getState$user === void 0 || (_store$getState$user = _store$getState$user.active) === null || _store$getState$user === void 0 || (_store$getState$user = _store$getState$user.content) === null || _store$getState$user === void 0 ? void 0 : _store$getState$user.savedViews) || {};
};

/**
 * Loads userPreferences and returns a default landing path, or null.
 * Uses Redux for savedViews so it does not depend on React context.
 */
export const resolveDefaultLandingUrlAfterAuth = async () => {
  var _preferencesContent2;
  let preferencesContent;
  try {
    preferencesContent = await readUserPreferencesContentForDefaultLanding();
  } catch (_unused) {
    return null;
  }
  const tryResolve = () => {
    var _preferencesContent;
    return resolveDefaultLandingPageUrl({
      defaultLandingPage: (_preferencesContent = preferencesContent) === null || _preferencesContent === void 0 ? void 0 : _preferencesContent.defaultLandingPage,
      preferencesContent,
      savedViews: getSavedViewsFromStore()
    });
  };
  let target = tryResolve();
  const dp = (_preferencesContent2 = preferencesContent) === null || _preferencesContent2 === void 0 ? void 0 : _preferencesContent2.defaultLandingPage;
  const wantsSavedView = (dp === null || dp === void 0 ? void 0 : dp.kind) === 'savedView' && typeof (dp === null || dp === void 0 ? void 0 : dp.key) === 'string' && dp.key.trim().length > 0;
  if (!target && wantsSavedView) {
    /* eslint-disable no-await-in-loop -- short poll for User.savedViews after login */
    for (let i = 0; i < SAVED_VIEW_POLL_COUNT; i += 1) {
      await sleep(SAVED_VIEW_POLL_MS);
      target = tryResolve();
      if (target) break;
    }
    /* eslint-enable no-await-in-loop */
  }
  return target && typeof target === 'string' ? target : null;
};

/**
 * After login, optionally navigate from `/` or `/file-cabinet` to userPreferences default.
 */
const PostAuthDefaultLanding = () => {
  var _login$user2;
  const login = useSelector(s => s.login);
  const completedForUserRef = useRef(null);
  const inFlightRef = useRef(false);

  // Only mounted while authenticated; reset gate on unmount (logout), not via isAuthenticated.
  useEffect(() => () => {
    completedForUserRef.current = null;
    resetLandingGate();
  }, []);
  useLayoutEffect(() => {
    var _login$user;
    if (!login.isAuthenticated) {
      return undefined;
    }
    const uid = (_login$user = login.user) === null || _login$user === void 0 ? void 0 : _login$user.user_id;
    if (!uid) {
      return undefined;
    }
    if (completedForUserRef.current === uid) {
      return undefined;
    }
    if (inFlightRef.current) {
      return undefined;
    }
    const pathname = appHistory.location.pathname;
    if (pathname !== '/' && pathname !== '/file-cabinet') {
      return undefined;
    }
    inFlightRef.current = true;
    let cancelled = false;
    if (getLandingGateSnapshot() === 'idle') {
      startLandingCheck();
    }
    (async () => {
      try {
        const target = await resolveDefaultLandingUrlAfterAuth();
        if (!cancelled && target) {
          appHistory.replace(target);
        }
      } finally {
        if (!cancelled) {
          finishLandingCheck();
          inFlightRef.current = false;
          completedForUserRef.current = uid;
        }
      }
    })();
    return () => {
      cancelled = true;
      inFlightRef.current = false;
      if (getLandingGateSnapshot() === 'checking') {
        finishLandingCheck();
      }
    };
  }, [login.isAuthenticated, (_login$user2 = login.user) === null || _login$user2 === void 0 ? void 0 : _login$user2.user_id]);
  return null;
};
export default PostAuthDefaultLanding;